import { React, useState, useEffect, createContext, useMemo, useRef } from "react";
import request from "../../../Helpers/API";
import { Autocomplete, Button, Modal, Box, TextField, MenuItem, InputAdornment, FormControl, InputLabel, Select } from "@mui/material";
import Swal from "sweetalert2";
import PlantillaScreen from "../../helpers/PlantillaScreen";
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import { capitalizePalabras } from "../../../Helpers/CapitalizePalabras";

const regexCaracterValido = /[:/a-zA-Z0-9ñÑáéíóúÁÉÍÓÚ.,()#$% \n]/;

const describir = (char) => {
    const nombres = {
        '\n': 'salto de línea',
        '\t': 'tabulación',
        ' ': 'espacio',
    };
    return nombres[char] ?? `"${char}"`;
};

const encontrarCaracterNoPermitido = (value) => {
    return [...value].find(char => !regexCaracterValido.test(char));
};

export const EditarModeloModal = ({ open, onClose, id, departamento }) => {
    const [loader, setLoader] = useState({
        mostrar: false,
        alerta: false,
        error: false,
        mensaje: "",
    });
    const [categorias, setCategorias] = useState([]);

    const [infoModelo, setInfoModelo] = useState({
        idlinea: "",
        modelo: "",
        descripcionMdl: "",
        costo_refacciones: 0,
        costo_servicio: 0,
        precioLista: 0,
        moneda: "",
        categoria: 0,
        usuario: 0,
        caracteristicas: [""],
        imagenes: Array(5).fill(null),
    });


    useEffect(() => {
        setLoader({ ...loader, mostrar: true });

        const getModelo = request.get(`ListadoModelos/ObtenerModelosID?idmodelo=${id}`);
        const getCarrusel = request.get(`ListadoModelos/ObtenerCarrusel?idmodelo=${id}`);

        Promise.all([getModelo, getCarrusel])
            .then(([modeloRes, carruselRes]) => {
                const modelo = modeloRes[0];

                let caracteristicas = [];
                try {
                    const parsed = JSON.parse(modelo.caracteristicas);
                    caracteristicas = parsed.map(c => c.descripcion);
                } catch (error) {
                    console.warn("Error al parsear caracteristicas:", error);
                    caracteristicas = [""];
                }

                const imagenesLimpias = carruselRes.map((item) => {
                    try {
                        const parsed = JSON.parse(item.imagen);
                        return {
                            idmodelo: item.idmodelo || null,
                            numero: item.numero || null,
                            documento: parsed.documento,
                            extension: "jpeg"
                        };
                    } catch (error) {
                        return null;
                    }
                }).filter(Boolean);

                setInfoModelo({
                    idlinea: modelo.idlinea,
                    modelo: modelo.modelo,
                    caracteristicas: caracteristicas,
                    imagenes: [
                        ...imagenesLimpias,
                        ...Array(Math.max(0, 5 - imagenesLimpias.length)).fill(null)
                    ],
                    descripcionMdl: modelo.mdl_descripcion,
                    costo_refacciones: modelo.costo_refacciones,
                    costo_servicio: modelo.costo_servicios,
                    precioLista: modelo.precio_lista,
                    moneda: modelo.moneda,
                    categoria: modelo.categoria
                });

                setLoader({ ...loader, mostrar: false });
            })
            .catch((error) => {
                setLoader({ ...loader, mostrar: false });
                console.error("Error al obtener modelo o carrusel:", error);
            });

    }, [id]);


    useEffect(() => {
        if (departamento === 3) {
            setInfoModelo(prev => ({
                ...prev,
                precioLista: parseFloat(prev.costo_refacciones || 0) + parseFloat(prev.costo_servicio || 0)
            }));
        }
    }, [infoModelo.costo_refacciones, infoModelo.costo_servicio, departamento]);


    const fileInputsRef = useRef([]);
    //Funciones de Caracteristicas
    const handleCaracteristicaChange = (index, value) => {
        const nuevas = [...infoModelo.caracteristicas];
        nuevas[index] = value;
        setInfoModelo({
            ...infoModelo,
            caracteristicas: nuevas,
        });
    };

    const agregarCaracteristica = () => {
        setInfoModelo({
            ...infoModelo,
            caracteristicas: [...infoModelo.caracteristicas, ""],
        });
    };

    const quitarUltimaCaracteristica = () => {
        if (infoModelo.caracteristicas.length > 1) {
            setInfoModelo({
                ...infoModelo,
                caracteristicas: infoModelo.caracteristicas.slice(0, -1),
            });
        }
    };
    //Fin de Funciones de Caracteristicas

    //Cargar Select de Lineas
    useEffect(() => {
        setLoader({ ...loader, mostrar: true });
        request
            .get(
                `ListadoModelos/CategoriasDropDownList`
            )
            .then((result) => {
                setLoader({ ...loader, mostrar: false });
                setCategorias(result);
                return;
            })
            .catch((error) => {
                setLoader({ ...loader, mostrar: false });
                return;
            });
        setLoader({ ...loader, mostrar: false });
    }, []);


    //Funciones de selector de imagenes
    const abrirSelector = (index) => fileInputsRef.current[index]?.click();

    const GetFile = (event, index) => {
        const file = event.target.files[0];
        if (!file) return;

        const formatosValidos = ["jpg", "jpeg", "png"];
        const extension = file.name.split(".").pop().toLowerCase();

        if (!formatosValidos.includes(extension)) {
            Swal.fire("Nuevo Modelo", "Formato de archivo no válido", "error");
            event.target.value = "";
            return;
        }

        const fReader = new FileReader();
        fReader.readAsDataURL(file);
        fReader.onloadend = function (event) {
            const nuevaImagen = {
                documento: event.target.result,
                // extension,
                // nueva: true
            };
            const nuevasImagenes = [...infoModelo.imagenes];
            nuevasImagenes[index] = nuevaImagen;
            setInfoModelo({ ...infoModelo, imagenes: nuevasImagenes });
        };
    };

    const eliminarImagen = async (index) => {
        const imagen = infoModelo.imagenes[index];

        if (!imagen) return; // Por si ya es null
        if (!imagen.nueva) {
            const confirmacion = await Swal.fire({
                title: '¿Eliminar imagen?',
                text: "Esta acción no se puede deshacer",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar',
            });

            if (!confirmacion.isConfirmed) return;

            try {
                await request.get(`ListadoModelos/EliminarFotografia?idmodelo=${imagen.idmodelo}&numero=${imagen.numero}`);
                Swal.fire("Éxito", "Imagen eliminada correctamente", "success");
            } catch (error) {
                Swal.fire("Error", "No se pudo eliminar la imagen", "error");
                return;
            }
        }

        const nuevasImagenes = [...infoModelo.imagenes];
        nuevasImagenes[index] = null;

        setInfoModelo({
            ...infoModelo,
            imagenes: nuevasImagenes
        });
    };

    //Fin de selector de imagenes

    // console.log(infoModelo)

    const EditarModelo = () => {
        const regexPermitidosModelo = /^[:/a-zA-Z0-9ñÑáéíóúÁÉÍÓÚ.,()#$% \n]*$/;

        // VALIDAR MODELO
        if (!infoModelo.modelo) {
            Swal.fire({
                title: "MODELOS",
                text: "Debe escribir una descripción para el Modelo",
                icon: "warning",
                customClass: { container: "mi-superpoop" },
            });
            return;
        }

        if (!regexPermitidosModelo.test(infoModelo.modelo) || infoModelo.modelo.length > 100) {
            const charInvalido = encontrarCaracterNoPermitido(infoModelo.modelo);
            Swal.fire({
                title: "MODELOS",
                text: charInvalido
                    ? `El campo Modelo contiene el carácter no permitido: ${describir(charInvalido)}`
                    : `El campo Modelo excede los 100 caracteres permitidos`,
                icon: "warning",
                customClass: { container: "mi-superpoop" },
            });
            return;
        }

        // VALIDAR DESCRIPCION
        if (!infoModelo.descripcionMdl) {
            Swal.fire({
                title: "MODELOS",
                text: "Debe ingresar una descripción para el modelo",
                icon: "warning",
                customClass: { container: "mi-superpoop" },
            });
            return;
        }

        if (!regexPermitidosModelo.test(infoModelo.descripcionMdl) || infoModelo.descripcionMdl.length > 100) {
            const charInvalido = encontrarCaracterNoPermitido(infoModelo.descripcionMdl);
            Swal.fire({
                title: "MODELOS",
                text: charInvalido
                    ? `El campo Descripción contiene el carácter no permitido: ${describir(charInvalido)}`
                    : `El campo Descripción excede los 100 caracteres permitidos`,
                icon: "warning",
                customClass: { container: "mi-superpoop" },
            });
            return;
        }

        // VALIDAR PRECIO DE LISTA
        if (!infoModelo.precioLista || infoModelo.precioLista <= 0) {
            Swal.fire({
                title: "MODELOS",
                text: "Debe ingresar un precio de lista mayor a 0",
                icon: "warning",
                customClass: { container: "mi-superpoop" },
            });
            return;
        }

        // VALIDAR CARACTERISTICAS
        if (!Array.isArray(infoModelo.caracteristicas) || infoModelo.caracteristicas.length === 0) {
            Swal.fire({
                title: "MODELOS",
                text: "Debe ingresar al menos una característica",
                icon: "warning",
                customClass: { container: "mi-superpoop" },
            });
            return;
        }
        // VALIDAR CARACTERISTICAS (índice 0)
        const caracteristicaInvalida = !regexPermitidosModelo.test(infoModelo.caracteristicas[0] || "");

        if (caracteristicaInvalida) {
            const charInvalido = encontrarCaracterNoPermitido(infoModelo.caracteristicas[0] || "");
            Swal.fire({
                title: "MODELOS",
                text: charInvalido
                    ? `El campo Características contiene el carácter no permitido: ${describir(charInvalido)}`
                    : `El campo Características contiene caracteres no permitidos`,
                icon: "warning",
                customClass: { container: "mi-superpoop" },
            });
            return;
        }

        // VALIDAR PUNTOS DE VALOR (índice 1)
        const puntosInvalidos = !regexPermitidosModelo.test(infoModelo.caracteristicas[1] || "");

        if (puntosInvalidos) {
            const charInvalido = encontrarCaracterNoPermitido(infoModelo.caracteristicas[1] || "");
            Swal.fire({
                title: "MODELOS",
                text: charInvalido
                    ? `El campo Puntos de Valor contiene el carácter no permitido: ${describir(charInvalido)}`
                    : `El campo Puntos de Valor contiene caracteres no permitidos`,
                icon: "warning",
                customClass: { container: "mi-superpoop" },
            });
            return;
        }

        // GUARDAR
        setLoader({ ...loader, mostrar: true });
        const post = {
            idmodelo: id,
            idlinea: infoModelo.idlinea,
            modelo: infoModelo.modelo,
            descripcion_mdl: infoModelo.descripcionMdl,
            costo_refacciones: infoModelo.costo_refacciones,
            costo_servicios: infoModelo.costo_servicio,
            precio_lista: infoModelo.precioLista,
            moneda: infoModelo.moneda,
            categoria: infoModelo.categoria,
            usuario: infoModelo.usuario,
            caracteristicas: JSON.stringify(infoModelo.caracteristicas),
            imagenes: JSON.stringify(infoModelo.imagenes.filter(img => img && img.documento && !img.idmodelo))
        };

        request
            .post(`ListadoModelos/EditarModelo`, post)
            .then(async (result) => {
                setLoader({ ...loader, mostrar: false });
                onClose();
                await Swal.fire({
                    title: "MODELOS",
                    text: "Registro modificado con éxito",
                    icon: "success",
                    customClass: { container: "mi-superpoop" },
                });
            })
            .catch((error) => {
                setLoader({ ...loader, mostrar: false });
                Swal.fire("MODELOS", JSON.stringify(error), "error");
            });
    };
    // console.log (JSON.stringify(infoModelo.imagenes.filter(img => img !== null)))
    return (
        <>
            <Modal open={open} onClose={onClose}>
                <div className="modelos-modal-editar-formulario">
                    <div className="modelos-modal-plantilla">
                        <div className="plantilla-screen-filtro-container" id="mdlFiltros-modal">
                            <PlantillaScreen
                                titulo={"EDITAR MODELO"}
                                className_main={1}
                                className={"plantilla-screen-filtro-container-modal"}
                            >
                                <div className="cerrar-modal-button" onClick={onClose}>X</div>
                                <div className={`modelos-modal-agregar-convenio`}>
                                    <div className="modelos-modal-agregar-convenio-container">
                                        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                            <TextField
                                                id="outlined-multiline-static"
                                                label="Modelo"
                                                size="small"
                                                value={infoModelo.modelo}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    const regex = /^[a-zA-Z0-9ñÑáéíóúÁÉÍÓÚ.,()#$% ]*$/;
                                                    if (regex.test(value)) {
                                                        setInfoModelo({
                                                            ...infoModelo,
                                                            modelo: value,
                                                        });
                                                    }
                                                }}
                                                sx={{
                                                    flex: 2, // 2 partes de 4 (50%)
                                                    '& .MuiInputBase-root': {
                                                        height: '40px',
                                                    },
                                                    [`@media (max-height:499px)`]: {
                                                        '& .MuiInputBase-root': {
                                                            height: '28px',
                                                        },
                                                        '& input': {
                                                            padding: '4px 6px',
                                                        },
                                                    },
                                                }}
                                            />
                                            {departamento !== 3 && (
                                                <TextField
                                                    label="Precio de lista"
                                                    size="small"
                                                    value={infoModelo.precioLista}
                                                    onChange={(e) => {
                                                        const valor = e.target.value;
                                                        if (/^\d*\.?\d*$/.test(valor)) {
                                                            setInfoModelo({
                                                                ...infoModelo,
                                                                precioLista: valor,
                                                            });
                                                        }
                                                    }}
                                                    sx={{
                                                        flex: 1, // 1 parte de 4 (25%)
                                                        '& .MuiInputBase-root': {
                                                            height: '40px',
                                                        },
                                                        [`@media (max-height:499px)`]: {
                                                            '& .MuiInputBase-root': {
                                                                height: '28px',
                                                            },
                                                            '& input': {
                                                                padding: '6px 8px',
                                                            },
                                                        },
                                                    }}
                                                />
                                            )}
                                            <FormControl
                                                size="small"
                                                sx={{
                                                    flex: 1, // 1 parte de 4 (25%)
                                                    '& .MuiInputBase-root': {
                                                        height: '40px', // Igual que los otros inputs
                                                    },
                                                    [`@media (max-height:499px)`]: {
                                                        '& .MuiInputBase-root': {
                                                            height: '28px',
                                                        },
                                                    },
                                                }}
                                            >
                                                <InputLabel id="moneda-label">Moneda</InputLabel>
                                                <Select
                                                    labelId="moneda-label"
                                                    id="select-moneda"
                                                    value={infoModelo.moneda}
                                                    label="Moneda"
                                                    onChange={(e) => {
                                                        const valor = e.target.value;
                                                        setInfoModelo({
                                                            ...infoModelo,
                                                            moneda: valor,
                                                        });
                                                    }}
                                                >
                                                    <MenuItem value={'MXN'}>Pesos</MenuItem>
                                                    <MenuItem value={'USD'}>Dólares</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Box>
                                        {departamento === 3 && (
                                            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                                <TextField
                                                    label="Costo Refacciones"
                                                    size="small"
                                                    value={infoModelo.costo_refacciones}
                                                    onChange={(e) => {
                                                        const valor = e.target.value;
                                                        if (/^\d*\.?\d*$/.test(valor)) {
                                                            setInfoModelo({
                                                                ...infoModelo,
                                                                costo_refacciones: valor,
                                                            });
                                                        }
                                                    }}
                                                    sx={{
                                                        flex: 1, // 1 parte de 4 (25%)
                                                        '& .MuiInputBase-root': {
                                                            height: '40px', // Igual que los otros inputs
                                                        },
                                                        [`@media (max-height:499px)`]: {
                                                            '& .MuiInputBase-root': {
                                                                height: '28px',
                                                            },
                                                        },
                                                    }}
                                                />
                                                <TextField
                                                    label="Costo Servicios"
                                                    size="small"
                                                    value={infoModelo.costo_servicio}
                                                    onChange={(e) => {
                                                        const valor = e.target.value;
                                                        if (/^\d*\.?\d*$/.test(valor)) {
                                                            setInfoModelo({
                                                                ...infoModelo,
                                                                costo_servicio: valor,
                                                            });
                                                        }
                                                    }}
                                                    sx={{
                                                        flex: 1, // 1 parte de 4 (25%)
                                                        '& .MuiInputBase-root': {
                                                            height: '40px', // Igual que los otros inputs
                                                        },
                                                        [`@media (max-height:499px)`]: {
                                                            '& .MuiInputBase-root': {
                                                                height: '28px',
                                                            },
                                                        },
                                                    }}
                                                />
                                                <TextField
                                                    label="Precio de Lista"
                                                    size="small"
                                                    value={infoModelo.precioLista}
                                                    InputProps={{
                                                        readOnly: true,
                                                    }}
                                                    sx={{
                                                        flex: 1, // 1 parte de 4 (25%)
                                                        '& .MuiInputBase-root': {
                                                            height: '40px', // Igual que los otros inputs
                                                        },
                                                        [`@media (max-height:499px)`]: {
                                                            '& .MuiInputBase-root': {
                                                                height: '28px',
                                                            },
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        )}
                                        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                            <TextField
                                                id="outlined-multiline-static"
                                                label="Descripción del modelo"
                                                size="small"
                                                fullWidth
                                                multiline
                                                rows={3}
                                                value={infoModelo.descripcionMdl}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    const regex = /^[/:a-zA-Z0-9ñÑáéíóúÁÉÍÓÚ.,()#$% ]*$/;

                                                    if (regex.test(value)) {
                                                        setInfoModelo({
                                                            ...infoModelo,
                                                            descripcionMdl: value,
                                                        });
                                                    }
                                                }}
                                                sx={{
                                                    height: '40px',
                                                    [`@media (max-height:499px)`]: {
                                                        '& .MuiInputBase-root': {
                                                            height: '28px',
                                                        },
                                                        '& input': {
                                                            padding: '6px 8px',
                                                        },
                                                    },
                                                }}
                                            />

                                        </Box>
                                        <div className="caracteristicas-section">
                                            <div className="caracteristicas-group">
                                                <h4>Características</h4>
                                                {/* <div className="botones-container">
                                                    {infoModelo.caracteristicas.length > 1 && (
                                                        <button type="button" onClick={quitarUltimaCaracteristica} className="quitar-caracteristica">
                                                            −
                                                        </button>
                                                    )}
                                                    <button type="button" onClick={agregarCaracteristica} className="agregar-caracteristica">+</button>
                                                </div> */}
                                            </div>
                                            <div className="caracteristicas-campos scroll-4">
                                                <TextField
                                                    key={0}
                                                    label={`Característica`}
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    sx={{
                                                        mt: 1,
                                                        [`@media (max-height:499px)`]: {
                                                            '& .MuiInputBase-root': {
                                                                height: '28px',
                                                            },
                                                            '& input': {
                                                                padding: '6px 8px',
                                                            },
                                                        },
                                                    }}
                                                    size="small"
                                                    value={infoModelo.caracteristicas[0] || ""}
                                                    onChange={(e) => {
                                                        const value = e.target.value;

                                                        // Limitar caracteres según regex
                                                        const regexCaracteristicas = /^[:/a-zA-Z0-9ñÑáéíóúÁÉÍÓÚ.,()#$% \n]*$/;


                                                        if (regexCaracteristicas.test(value) && value.length <= 2500) {
                                                            handleCaracteristicaChange(0, value);
                                                        }
                                                    }}
                                                />
                                            </div>

                                            <div style={{ marginTop: "-30px", marginBottom: "-5px" }}>
                                                <FormControl
                                                    size="small"
                                                    fullWidth
                                                    sx={{
                                                        '& .MuiInputBase-root': {
                                                            height: '40px',
                                                        },
                                                        '& .MuiInputLabel-root': {
                                                            top: '-2px',
                                                        },
                                                        [`@media (max-height:499px)`]: {
                                                            '& .MuiInputBase-root': {
                                                                height: '28px',
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <InputLabel id="categoria-label">Categoría</InputLabel>
                                                    <Select
                                                        labelId="categoria-label"
                                                        id="select-categoria"
                                                        value={infoModelo.categoria}
                                                        label="Categoría"
                                                        onChange={(e) =>
                                                            setInfoModelo({
                                                                ...infoModelo,
                                                                categoria: e.target.value,
                                                            })
                                                        }
                                                    >
                                                        <MenuItem value={0}>Sin categoría</MenuItem>
                                                        {categorias.map((cat) => (
                                                            <MenuItem key={cat.id_categoria} value={cat.id_categoria}>
                                                                {cat.descripcion}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </div>
                                            <div className="puntos-valor-campos">
                                                <h4>Puntos de Valor</h4>
                                                {/* {infoModelo.caracteristicas.map((carac, index) => ( */}
                                                <TextField
                                                    key={1}
                                                    label={`Puntos de Valor`}
                                                    multiline
                                                    rows={3}
                                                    fullWidth
                                                    sx={{
                                                        mt: 0,
                                                        [`@media (max-height:499px)`]: {
                                                            '& .MuiInputBase-root': {
                                                                height: '28px',
                                                            },
                                                            '& input': {
                                                                padding: '6px 8px',
                                                            },
                                                        },
                                                    }}
                                                    size="small"
                                                    value={infoModelo.caracteristicas[1] || ""}
                                                    onChange={(e) => handleCaracteristicaChange(1, e.target.value)}

                                                />
                                            </div>
                                        </div>


                                        <div className="imagenes-section">
                                            <div className="titulos-imagenes-section">
                                                <span><b>Imágenes del modelo</b></span>
                                                <span className="titulo-derecha" >Máximo 5 imágenes</span>
                                            </div>
                                            <div className="imagenes-group">
                                                {infoModelo.imagenes.map((img, index) => (
                                                    <div
                                                        key={index}
                                                        className="imagen-button"
                                                        onClick={() => !img && abrirSelector(index)}
                                                    >
                                                        {img ? (
                                                            <>
                                                                <img
                                                                    className="imagen-cargada"
                                                                    src={img.documento}
                                                                    alt={`imagen-${index}`}
                                                                // style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: 6 }}
                                                                />
                                                                <DeleteIcon

                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        eliminarImagen(index);
                                                                    }}
                                                                    style={{
                                                                        position: "absolute",
                                                                        top: 2,
                                                                        right: 2,
                                                                        color: "red",
                                                                        backgroundColor: "white",
                                                                        borderRadius: "50%",
                                                                        fontSize: "1.2rem"
                                                                    }}
                                                                />
                                                            </>
                                                        ) : (
                                                            <AddPhotoAlternateIcon style={{ fontSize: 40, color: "#bbb" }} />
                                                        )}
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            style={{ display: "none" }}
                                                            ref={(el) => (fileInputsRef.current[index] = el)}
                                                            onChange={(e) => GetFile(e, index)}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="modelos-modal-buttons">
                                        <button
                                            className="modelos-modal-button-guardar"
                                            onClick={() => EditarModelo()}
                                        >
                                            Guardar Cambios
                                        </button>
                                    </div>
                                </div>
                            </PlantillaScreen>
                        </div>

                    </div>
                </div >
            </Modal >
        </>
    );
};